import * as R from 'ramda'

import { GetEncodedDispensaryPromosQuery } from '../../../../graphql/camel'
import { findAsyncSeq } from '../../../../utils/collectionTools'
import { CartDispensaryPromoCode } from '../../dispensary-promo'
import { tryTranslateCodeForPromos } from './translateCode'
import { sanitizeInput } from './util'

/**
 * Given a set of encoded dispensary promos and a set of applied (to cart) dispensary promos,
 * find the first applied dispensary promo that translates to the specified root promo code.
 *
 * Note: applied dispensary codes are stored in plain text on the customer's cart.
 */
export const findDispensaryPromoTranslatingTo =
  (promos: NonNullable<GetEncodedDispensaryPromosQuery['getEncodedDispensaryPromos']>['promos']) =>
  (
    cartDispensaryPromoCodes: ReadonlyArray<CartDispensaryPromoCode>,
  ): ((rootPromo: string) => Promise<CartDispensaryPromoCode | undefined>) => {
    const tryTranslateCode = tryTranslateCodeForPromos(promos)

    // predicate for searching dispensary promos stored in cart attribute
    // - translates dispensary promo code to root promo
    const dispensaryPromoTranslatesTo = (
      rootPromo: string,
    ): ((entry: CartDispensaryPromoCode) => Promise<boolean>) =>
      R.pipe(
        ({ dispensaryCode }) => dispensaryCode,
        // try translating dispensary promo code entry to root promo
        tryTranslateCode,
        // return true if entry translated successfully and result equals input text
        R.andThen((translated) => !!translated && translated === rootPromo),
      )

    return R.pipe(sanitizeInput, (sanitized) =>
      findAsyncSeq(dispensaryPromoTranslatesTo(sanitized))(cartDispensaryPromoCodes),
    )
  }
